import { ECustomSummmarizedflow } from '@/constants/customSummarizedFlow'
import { DISEASES } from '@/constants/disease'
import { MEDICINE_CATEGORIES } from '@/constants/medicine'
import { util } from '@/plugins/util'
import store from '@/store'
import { isEmpty, isNil } from 'lodash'
import moment from 'moment'

const rules = {}

// ===================================================================

rules[DISEASES.CSU] = {
  inclusion: {
    otimizar_posologia_anti_histaminico: {
      label: 'O médico selecionou a opção de otimizar posologia de anti-histamínicos.',
      key: 'otimizar_posologia_anti_histaminico',
      check: function (attendance) {
        return attendance?.data?.steps?.optimize === true
      },
    },
    adicionar_omalizumabe_anti_histaminico: {
      label: 'O médico selecionou a opção de adicionar Omalizumabe ao anti-histamínico.',
      key: 'adicionar_omalizumabe_anti_histaminico',
      check: function (attendance) {
        return attendance?.data?.steps?.omalizumabe === true
      },
    },
  },
}

// ===================================================================

rules[DISEASES.ESPONDILOARTRITIS] = {
  exclusion: {
    intolerancia_contraindicacao_aines: {
      label: 'O paciente tem intolerância ou contraindicação a AINES',
      key: 'intolerancia_contraindicacao_aines',
      check: function (attendance) {
        return attendance?.data?.historico_medicamentoso?.profile?.contraindicacao_aines?.value === true
      },
    },
    dois_ou_mais_antiinflamatorios_tres_meses_uso: {
      label: 'O paciente usa ou já usou pelo menos dois antiinflamatórios por pelo menos 3 meses',
      key: 'dois_ou_mais_antiinflamatorios_tres_meses_uso',
      check: function (attendance) {
        if (!attendance?.data?.historico_medicamentoso?.medicines) {
          return false
        }
        return (
          Object.values(attendance?.data?.historico_medicamentoso?.medicines)?.filter(
            (m) =>
              m.medicine.category.id === MEDICINE_CATEGORIES.ANTI_INFLAMATORIOS &&
              m.periods.some((p) => {
                let endMoment = p.end ? moment.utc(p.end) : moment.utc()

                return endMoment.diff(moment(p.start), 'months') >= 3
              }),
          )?.length >= 2
        )
      },
    },
    entesite_ou_dactilite: {
      label: 'O paciente apresenta sintoma de entesite ou dactilite',
      key: 'entesite_ou_dactilite',
      check: function (attendance) {
        return (
          attendance?.data?.sintomas?.symptoms?.dactilite?.value === true ||
          attendance?.data?.sintomas?.symptoms?.entesite?.value === true
        )
      },
    },
    doenca_inflamatoria_intestinal_ou_uveite: {
      label: 'O paciente apresenta doença inflamatória intestinal ou uveíte',
      key: 'doenca_inflamatoria_intestinal_ou_uveite',
      check: function (attendance) {
        return (
          attendance?.data?.sintomas?.symptoms?.uveite?.value === true ||
          attendance?.data?.investigacao_pessoal?.profile?.uveite?.value === true ||
          attendance?.data?.investigacao_pessoal?.diseases?.includes('doenca_inflamatoria_intestinal')
        )
      },
    },
    insuficiencia_cardiaca_grau_iii_iv: {
      label: 'O paciente apresenta insuficiência cardíaca de grau III ou IV',
      key: 'insuficiencia_cardiaca_grau_iii_iv',
      check: function (attendance) {
        return attendance?.data?.investigacao_pessoal?.diseases?.includes('insuficiencia_cardiaca_grau_iii_iv')
      },
    },
    esclerose_multipla_doenca_desmielinizante: {
      label: 'O paciente apresenta esclerose múltipla ou doença desmielinizante',
      key: 'esclerose_multipla_doenca_desmielinizante',
      check: function (attendance) {
        return attendance?.data?.investigacao_pessoal?.diseases?.includes('esclerose_multipla_doenca_desmielinizante')
      },
    },
    esclerose_multipla_familia: {
      label: 'O paciente histórico familiar de esclerose múltipla',
      key: 'esclerose_multipla_familia',
      check: function (attendance) {
        return attendance?.data?.investigacao_familiar?.profile?.esclerose_multipla?.value === true
      },
    },
    psoriase: {
      label: 'O paciente apresenta sintomas de psoríase',
      key: 'psoriase',
      check: function (attendance) {
        return (
          attendance?.data?.sintomas?.symptoms?.psoriase?.value === true ||
          (attendance?.data?.asas?.caspar_psoriase === true &&
            attendance?.data?.asas?.caspar_condicao_psoriase === 'ATUAL')
        )
      },
    },
  },
  inclusion: {
    asas_axial_positivo: {
      label: 'ASAS Axial positivo',
      key: 'asas_axial_positivo',
      check: function (attendance) {
        return attendance?.summary?.asasAxial?.value === '+'
      },
    },
    asas_periferico_positivo: {
      label: 'ASAS Periférico positivo',
      key: 'asas_periferico_positivo',
      check: function (attendance) {
        return attendance?.summary?.asasPeriferico?.value === '+'
      },
    },
  },
}

// ===================================================================

rules[DISEASES.PSORIASIS] = {
  exclusion: {
    artrite_psoriatica: {
      label: 'Possui artrite psoriática',
      key: 'artrite_psoriatica',
      check: function (attendance) {
        return (
          attendance?.data?.investigacao_articular?.diagnostico_artrite_psoriatica?.value === 'SIM' ||
          (attendance?.data?.investigacao_articular?.diagnostico_artrite_psoriatica?.value === 'NAO_SEI' &&
            (parseInt(attendance?.summary?.caspar?.value) > 2 || parseInt(attendance?.summary?.pest?.value) > 2))
        )
      },
    },
    alteracoes_intestinais: {
      label:
        'Tem diarréia crônica, dor abdominal ou outras alterações de hábito intestinal; OU possui doença inflamatoria intestinal',
      key: 'alteracoes_intestinais',
      check: function (attendance) {
        return (
          attendance?.data?.investigacao_pessoal?.diseases?.includes('doenca_inflamatoria_intestinal') ||
          attendance?.data?.investigacao_pessoal?.profile?.diarreia_cronica?.value === true
        )
      },
    },
    vinte_sessoes_fototerapia: {
      label: 'Fez pelo menos 20 sessões de fototerapia.',
      key: 'vinte_sessoes_fototerapia',
      check: function (attendance) {
        return parseInt(attendance?.data?.dados_especificos?.numero_sessoes_fototerapia?.value) >= 20
      },
    },
    exclusao_metotrexato: {
      label: 'Usou 15mg/semana de METOTREXATO por 8 semanas.',
      key: 'exclusao_metotrexato',
      check: function (attendance) {
        if (!attendance?.data?.dados_especificos?.medicines) {
          return false
        }
        return (
          Object.values(attendance?.data?.dados_especificos?.medicines)?.filter(
            (m) =>
              m.medicine.name.toLowerCase().includes('metotrexato') &&
              m.periods.some((p) => {
                if (p.period === 'CADA_N_SEMANAS') {
                  return false
                }
                let endMoment = p.end ? moment.utc(p.end) : moment.utc()
                let dosageFactor = p.period === 'POR_DIA' ? 1 : 7

                return endMoment.diff(moment(p.start), 'week') >= 8 && Number(p.dosage) * dosageFactor >= 15
              }),
          )?.length > 0
        )
      },
    },
    exclusao_acitretina: {
      label: 'Usou ACITRETINA pelo menos 25mg por 3 meses para adulto ou 0,5 mg/kg por 3 meses para crianças.',
      key: 'exclusao_acitretina',
      check: function (attendance) {
        if (!attendance?.data?.dados_especificos?.medicines) {
          return false
        }

        let maxDosage =
          util.toAgeNumber(attendance?.patient?.birthday) >= 18
            ? 25
            : 0.5 * Number(attendance?.patient?.currentWeightKg)

        return (
          Object.values(attendance?.data?.dados_especificos?.medicines)?.filter(
            (m) =>
              m.medicine.name.toLowerCase().includes('acitretina') &&
              m.periods.some((p) => {
                if (p.period === 'CADA_N_SEMANAS') {
                  return false
                }
                let endMoment = p.end ? moment.utc(p.end) : moment.utc()
                let dosageFactor = p.period === 'POR_DIA' ? 1 : 7

                return (
                  endMoment.diff(moment(p.start), 'month') >= 3 && Number(p.dosage) * dosageFactor >= Number(maxDosage)
                )
              }),
          )?.length > 0
        )
      },
    },
    insuficiencia_cardiaca_grau_iii_iv_ou_esclerose_multipla: {
      label:
        'Possui insuficiência cardíaca grau III ou IV; OU Possui esclerose multipla (antecedente pessoal ou antecedente familiar)',
      key: 'insuficiencia_cardiaca_grau_iii_iv_ou_esclerose_multipla',
      check: function (attendance) {
        return (
          attendance?.data?.investigacao_pessoal?.diseases?.includes('insuficiencia_cardiaca_grau_iii_iv') ||
          attendance?.data?.investigacao_pessoal?.diseases?.includes('esclerose_multipla_doenca_desmielinizante') ||
          attendance?.data?.investigacao_familiar?.profile?.esclerose_multipla?.value === true
        )
      },
    },
    cancer_pele: {
      label: 'Já teve câncer de pele?',
      key: 'cancer_pele',
      check: function (attendance) {
        return attendance?.data?.investigacao_pessoal?.profile?.cancer_pele?.value === true
      },
    },
  },
  inclusion: {
    psoriase_leve: {
      label: 'Psoríase Leve (PASI, BSA e DLQI menor que 10)',
      key: 'psoriase_leve',
      check: function (attendance) {
        return (
          Number(attendance?.data?.pasi_bsa?.pasi) < 10 &&
          Number(attendance?.data?.pasi_bsa?.bsa) < 10 &&
          Number(attendance?.data?.dlqi?.dlqi.value) < 10 &&
          attendance?.data?.areas_afetadas?.medicamentos_previos_mais_de_3_meses !== 'SIM_SEM_MELHORIA'
        )
      },
    },
    psoriase_moderada_grave: {
      label: 'Psoríase Moderada a Grave (PASI, BSA ou DLQI maior ou igual a 10)',
      key: 'psoriase_moderada_grave',
      check: function (attendance) {
        return (
          Number(attendance?.data?.pasi_bsa?.pasi) >= 10 ||
          Number(attendance?.data?.pasi_bsa?.bsa) >= 10 ||
          Number(attendance?.data?.dlqi?.dlqi.value) >= 10 ||
          attendance?.data?.areas_afetadas?.medicamentos_previos_mais_de_3_meses === 'SIM_SEM_MELHORIA'
        )
      },
    },
  },
}

// ===================================================================

rules[DISEASES.ATOPIC_DERMATITIS] = {
  flowExclusion: {
    summarized_sanofi_flow: {
      label: 'Excluir do fluxo resumido da SANOFI.',
      key: 'summarized_sanofi_flow',
      check: function (attendance) {
        return false
      },
    },
    exclude_from_all_list_sanofi_flow: {
      label: 'Não exibir quando a opção Mostrar Todos estiver marcada para a o fluxo da Sanofi.',
      key: 'exclude_from_all_list_sanofi_flow',
      check: function (attendance) {
        return store.getters['auth/pharmaceuticalDomainConfig'].CUSTOMSUMMARIZEDFLOW != ECustomSummmarizedflow.SANOFI
      },
    },
    show_on_treatments_sanofi_phase: {
      label: 'Exibir na fase de tratamentos do fluxo resumido da Sanofi.',
      key: 'show_on_treatments_sanofi_phase',
      check: function (attendance) {
        if (store.getters['auth/pharmaceuticalDomainConfig'].CUSTOMSUMMARIZEDFLOW == ECustomSummmarizedflow.SANOFI) {
          return attendance.data.summarizedFlow ?? false
        }
        return true
      },
    },
  },
}

// ===================================================================

rules[DISEASES.ASTHMA] = {
  inclusion: {
    acute_hypothesis: {
      label: 'Foi considerada "grave" na hipótese diagnóstica',
      key: 'acute_hypothesis',
      check: ({ parameters }) => {
        return (parameters['asthma:hypothesis']?.label?.diagnoses ?? []).includes('grave')
      },
    },
    eosinophilic_hypothesis: {
      label: 'Foi considerada "eosinofílica" na hipótese diagnóstica',
      key: 'eosinophilic_hypothesis',
      check: ({ parameters }) => {
        return (parameters['asthma:hypothesis']?.label?.diagnoses ?? []).includes('eosinofílica')
      },
    },
    alergic_hypothesis: {
      label: 'Foi considerada "alérgica" na hipótese diagnóstica',
      key: 'alergic_hypothesis',
      check: ({ parameters }) => {
        return (parameters['asthma:hypothesis']?.label?.diagnoses ?? []).includes('alérgica')
      },
    },
    '30-1500_on_question_37': {
      label: 'IgE total sérica (UI/ml) entre 30 e 1.500',
      key: '30-1500_on_question_37',
      check: ({ answers }) => {
        return answers['37'] === '30-1500'
      },
    },
    answered_questions_10_or_11: {
      label:
        'Faz ou já fez uso de "Combinação de corticoide inalatório + beta-2 agonista de longa duração (LABA)" e/ou "Corticoide inalatório (IC)"',
      key: 'answered_questions_10_or_11',
      check: ({ answers }) => {
        return !isEmpty(answers['10']) || !isEmpty(answers['11'])
      },
    },
    uncontrolled_asthma: {
      label: 'Asma parciamlmente controlada ou não controlada',
      key: 'uncontrolled_asthma',
      check: ({ parameters }) => {
        const desiredTexts = ['Asma parcialmente controlada', 'Asma não controlada']
        /** @type {string[]} */
        const targetTexts = [parameters['act']?.label, parameters['gina']?.label].filter((it) => !isNil(it))

        return desiredTexts.some((desiredText) => targetTexts.some((targetText) => targetText.includes(desiredText)))
      },
    },
    eosinophilic_or_alergic_hypothesis_or_yes_on_39: {
      label:
        'Foi considerada "eosinofílica" e/ou "alérgica" na hipótese e/ou aumentou a fração exalada de óxido nítrico',
      check: ({ parameters, answers }) => {
        return (
          (parameters['asthma:hypothesis']?.label?.diagnoses ?? []).includes('eosinofílica') ||
          (parameters['asthma:hypothesis']?.label?.diagnoses ?? []).includes('alérgica') ||
          answers['39'] === 'sim'
        )
      },
    },
    inhaled_corticosteroid_for_12yo: {
      label:
        'Faz uso de corticóide inalatório em alta dose caso tenha 12 anos ou mais, ou em alta ou média dose caso tenha menos de 12 anos',
      key: 'inhaled_corticosteroid_for_12yo',
      check: ({ answers, patientData }) => {
        return (
          (answers['12'] === 'alta' && patientData.age >= 12) ||
          (['alta', 'media'].includes(answers['12']) && patientData.age < 12)
        )
      },
    },
    answererd_any_of_questions_13_14_15_16: {
      label:
        'Faz ou já fez uso de "Beta-2 agonista de longa duração (LABA)" e/ou "Antagonista muscarínico de longa duração (LAMA)" e/ou "Antileucotrieno" e/ou "Corticoide oral"',
      key: 'answererd_any_of_questions_13_14_15_16',
      check: ({ answers }) => {
        return !isEmpty(answers['13']) || !isEmpty(answers['14']) || !isEmpty(answers['15']) || !isEmpty(answers['16'])
      },
    },
  },
}

rules[DISEASES.GENERAL_CLINIC] = {
  exclusion: {},
  inclusion: {},
  flowExclusion: {},
}

export const TREATMENT_EXHIBITION_RULES = rules
